// Persistent cache for geometry.bin. Mesh topology is usually immutable
// within a cycle URL, but overview regeneration rewrites the same paths —
// so the cache key includes a content revision from manifest.json
// (tiles_revision / overview_content_revision). Survives page reloads via
// Cache Storage; falls back to a normal fetch when Cache API is unavailable.

const CACHE_NAME = 'shadowwatch-geometry-v2';
const LEGACY_CACHE_NAMES = ['shadowwatch-geometry-v1'];

let cachePromise = null;
/** @type {string} */
let contentRevision = '0';
let legacyCleared = false;

async function clearLegacyGeometryCaches() {
  if (legacyCleared || typeof caches === 'undefined') return;
  legacyCleared = true;
  for (const name of LEGACY_CACHE_NAMES) {
    try {
      await caches.delete(name);
    } catch {
      /* ignore */
    }
  }
}

function openGeometryCache() {
  if (typeof caches === 'undefined') return null;
  if (!cachePromise) {
    cachePromise = clearLegacyGeometryCaches()
      .then(() => caches.open(CACHE_NAME))
      .catch(err => {
        console.warn('geometry Cache API unavailable', err);
        cachePromise = null;
        return null;
      });
  }
  return cachePromise;
}

function msNow() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * Call after loading manifest so re-generated overviews don't collide with
 * stale Cache Storage entries for the same /z/x/y/geometry.bin URL.
 */
export function setGeometryContentRevision(revision) {
  contentRevision = revision == null || revision === '' ? '0' : String(revision);
}

export function getGeometryContentRevision() {
  return contentRevision;
}

function cacheRequestFor(url) {
  // Separate Cache API key from the network URL (server still sees clean path).
  return new Request(`${url}?swrev=${encodeURIComponent(contentRevision)}`);
}

/**
 * Drop a cached geometry entry (e.g. after scalar/geometry vertex mismatch).
 */
export async function invalidateGeometryCache(url) {
  const cache = await openGeometryCache();
  if (!cache) return;
  try {
    await cache.delete(cacheRequestFor(url));
    // Also clear legacy v1-style key (bare URL) if present in this cache.
    await cache.delete(url);
  } catch (err) {
    console.warn('geometry cache invalidate failed', url, err);
  }
}

/**
 * Fetch geometry.bin bytes, preferring Cache Storage then network.
 * Returns {buffer, source: 'cache'|'network', fetchMs, bytes}.
 *
 * @param {string} url
 * @param {{bypassCache?: boolean}} [opts]
 */
export async function fetchGeometryArrayBuffer(url, opts = {}) {
  const t0 = msNow();
  const bypass = Boolean(opts.bypassCache);
  const cache = bypass ? null : await openGeometryCache();
  const cacheKey = cacheRequestFor(url);

  if (cache) {
    try {
      const hit = await cache.match(cacheKey);
      if (hit && hit.ok) {
        const buffer = await hit.arrayBuffer();
        return {
          buffer,
          source: 'cache',
          fetchMs: msNow() - t0,
          bytes: buffer.byteLength
        };
      }
    } catch (err) {
      console.warn('geometry cache match failed', url, err);
    }
  }

  const netT0 = msNow();
  // bypass / mismatch retry: skip HTTP force-cache (server marks geometry immutable)
  const res = await fetch(url, {cache: bypass ? 'reload' : 'force-cache'});
  if (!res.ok) {
    throw new Error(`geometry fetch failed: ${url} -> ${res.status}`);
  }

  const storeCache = await openGeometryCache();
  if (storeCache) {
    try {
      await storeCache.put(cacheKey, res.clone());
    } catch (err) {
      console.warn('geometry cache put failed', url, err);
    }
  }

  const buffer = await res.arrayBuffer();
  return {
    buffer,
    source: 'network',
    fetchMs: msNow() - netT0,
    bytes: buffer.byteLength
  };
}
